import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Send, Loader2, MailX, UserCheck, FileText } from 'lucide-react';
import { KboTemplatesDialog } from './KboTemplatesDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIP_LABELS = { sans_email: 'sans email', ne_plus_contacter: 'ne plus contacter', deja_invite: 'déjà invitées (< 90 j)', deja_inscrit: 'déjà inscrites', plafond: 'au-delà du plafond' };
const skipText = (s) => Object.entries(s || {}).filter(([, n]) => n).map(([k, n]) => `${n} ${SKIP_LABELS[k] || k}`).join(', ');

export const KboInviteBar = ({ selected, filters, filteredTotal, onDone }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState('');
  const [editing, setEditing] = useState(false);
  const loadTemplates = async () => { try { const { data } = await axios.get(`${API}/admin/kbo-open-data/templates`, { withCredentials: true }); setTemplates(data.items); } catch (e) { console.error(e); } };

  const load = async () => {
    try { const { data } = await axios.get(`${API}/admin/kbo-open-data/invite/state`, { withCredentials: true }); setState(data); } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); loadTemplates(); }, []);
  useEffect(() => {
    if (state?.status !== 'sending') return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [state?.status]);
  useEffect(() => { if (state?.status === 'done') onDone?.(); }, [state?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (body, label) => {
    setBusy(true);
    try {
      const { data: prev } = await axios.post(`${API}/admin/kbo-open-data/invite`, { ...body, preview: true }, { withCredentials: true });
      if (!prev.selected) { toast.info(`Aucune invitation à envoyer${prev.skipped && Object.keys(prev.skipped).length ? ` (${skipText(prev.skipped)})` : ''}`); return; }
      const detail = skipText(prev.skipped);
      if (!window.confirm(`${label}\n\n${prev.selected} invitation(s) seront envoyées (plafond ${prev.cap} par action)${detail ? `\nIgnorées : ${detail}` : ''}.\n\nConfirmer l'envoi ?`)) return;
      const { data } = await axios.post(`${API}/admin/kbo-open-data/invite`, { ...body, template: template || null }, { withCredentials: true });
      toast.success(`${data.selected} invitation(s) en cours d'envoi`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); } finally { setBusy(false); }
  };

  const sending = state?.status === 'sending';
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-2" data-testid="kbo-invite-bar">
      <Button size="sm" onClick={() => invite({ enterprise_numbers: selected }, `Inviter les ${selected.length} entreprise(s) sélectionnée(s)`)} disabled={!selected.length || busy || sending} data-testid="kbo-invite-selected-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Inviter la sélection ({selected.length})
      </Button>
      <Button size="sm" variant="outline" onClick={() => invite(params, 'Inviter tous les résultats filtrés disposant d\'un email')} disabled={!filteredTotal || busy || sending} data-testid="kbo-invite-filtered-btn">
        <Send className="w-4 h-4 mr-1" /> Inviter tous les résultats filtrés avec email
      </Button>
      <select value={template} onChange={(e) => setTemplate(e.target.value)} className="input-field h-9 text-xs max-w-[260px]" data-testid="kbo-invite-template"><option value="">Modèle : automatique (selon métier)</option>{templates.map((t) => <option key={t.slug} value={t.slug}>Modèle : {t.name}</option>)}</select>
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="kbo-templates-btn"><FileText className="w-4 h-4 mr-1" /> Modèles</Button>
      <KboTemplatesDialog open={editing} onOpenChange={setEditing} onChanged={loadTemplates} />
      <span className="text-xs text-[#6B7280] flex items-center gap-1" data-testid="kbo-invite-state">
        {sending && <><Loader2 className="w-3 h-3 animate-spin" /> Envoi en cours : {state.sent ?? 0}/{state.total}</>}
        {state?.status === 'done' && `Dernier envoi : ${state.sent} envoyée(s)${state.failed ? `, ${state.failed} échec(s)` : ''}${state.finished_at ? ` · ${new Date(state.finished_at).toLocaleString('fr-BE')}` : ''}`}
        {state?.status === 'error' && <span className="text-red-600">Erreur : {state.error}</span>}
        {state && ` · ${(state.total_sent || 0).toLocaleString('fr-BE')} invitations au total`}
        {state?.opted_out ? <span className="inline-flex items-center gap-1"><MailX className="w-3 h-3" /> {state.opted_out} désinscrite(s)</span> : null}
        {state?.conversions ? <span className="inline-flex items-center gap-1 font-semibold text-[#1E3A8A]" data-testid="kbo-conversions"><UserCheck className="w-3 h-3" /> {state.conversions.invited} invitées → {state.conversions.converted} inscrites ({state.conversions.rate} %) · {state.conversions.registered} prospects déjà membres</span> : null}
      </span>
    </div>
  );
};
